import { useAuthenticate, useAuthenticateWithSSO } from "../api/api";

import { Button, Input } from "antd";
import { useFormik } from "formik";
import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useMessage } from "../components/notifications/message";
import { useAuthContext } from "../providers/auth.provider";
import { EXHUT_WEB_LOCAL_STORAGE_TOKEN } from "../utils/constants";
import { GoogleLogin } from "@react-oauth/google";

export default function LoginPage() {
  const [searchParams] = useSearchParams();

  const registerQueryParam = searchParams.get("register");
  const accountTerminatedQueryParam = searchParams.get("terminated");
  const { mutateAsync: mutateLoginWithSSOAsync } = useAuthenticateWithSSO();

  const { mutateAsync, isPending } = useAuthenticate();
  const navigate = useNavigate();
  const { notification, messageError } = useMessage();
  const { setUser } = useAuthContext();

  useEffect(() => {
    if (registerQueryParam === undefined) {
      return;
    }
  }, [registerQueryParam]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },

    validationSchema: Yup.object({
      email: Yup.string().required("Please enter email."),
      password: Yup.string().required("Please enter password."),
    }),
    onSubmit: (values) => {
      mutateAsync({ email: values.email, password: values.password })
        .then((result) => {
          if (result.sign) {
            const sign = result.sign;
            localStorage.setItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN, sign);
            setUser(result);
            navigate("/home");
          } else {
            throw new Error(
              `Whoops! Something went wrong on our side. Please try after sometime or contact the admin.`,
            );
          }
        })
        .catch((err) => {
          let message =
            err.message ||
            "The email or password you have entered is incorrect. Please try again.";
          if (message === "Failed to fetch") {
            message =
              "Whoops! Our servers appears to be down. Please try later.";
          }

          messageError(
            "Sorry. We are unable to sign you in with this credentials.",
          );
        });
    },
  });

  const handleLoginWithSSO = (token?: string) => {
    if (!token) {
      messageError("Whoops! Please try again.");
    }

    mutateLoginWithSSOAsync({ token: token!, type: "google" })
      .then((result) => {
        localStorage.setItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN, result.sign);
        setUser(result);
        navigate("/home");
      })
      .catch((err) => {
        messageError("Unable to login. Please use email and password.");
      });
  };

  return (
    <div className="min-h-screen max-w-7xl mx-auto py-12 flex justify-center">
      {/* Background decoration */}
      {notification}

      <div className="w-[400px]">
        {accountTerminatedQueryParam === "true" && (
          <div className="bg-red-300/20 border border-red-500 rounded-lg p-4 text-sm my-6">
            Your account has been removed. Thank you.
          </div>
        )}

        {/* Logo & Brand */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-600">Sign in to your account</p>
          {!!registerQueryParam && (
            <div className="p-3 bg-green-300/100 border-green-400 border rounded-lg my-3">
              Your email address has been verified. Please login to continue.
            </div>
          )}
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div className="space-y-2">
              Email
              <Input
                id="email"
                type="text"
                disabled={isPending}
                className="h-11 border-gray-300 focus-visible:ring-blue-500"
                {...formik.getFieldProps("email")}
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Password</span>
                <a
                  href="https://hub.expensehut.com/forgot-password"
                  rel="noreferrer"
                  target="_blank"
                  className="text-sm text-blue-600 hover:text-blue-700 transition"
                >
                  Forgot?
                </a>
              </div>
              <Input
                id="password"
                type="password"
                disabled={isPending}
                className="h-11 border-gray-300 focus-visible:ring-blue-500"
                {...formik.getFieldProps("password")}
              />
            </div>

            {/* Submit Button */}
            <Button
              type="primary"
              onClick={formik.submitForm}
              disabled={isPending}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {isPending ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">Sign In</span>
              )}
            </Button>
          </form>

          <div className="my-3 flex justify-center">
            <GoogleLogin
              text="signin"
              onSuccess={(credentialResponse) =>
                handleLoginWithSSO(credentialResponse.credential)
              }
              onError={() => {
                messageError("Google signin Failed");
              }}
            />
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Don't have an account?
              </span>
            </div>
          </div>

          {/* Sign Up Link */}
          <Link to="/register">
            <Button
              type="link"
              className="w-full h-11 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Create Account
            </Button>
          </Link>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-4">
          <Link
            to="/"
            className="text-sm text-blue-600 hover:text-blue-700 transition"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
